import { skipToken } from "@reduxjs/toolkit/query";
import type { PageContextDescriptor } from "@repo/app-types/agents/pageContext";
import {
  collectPlaybookCriteria,
  getOutcomeDefinitionFromPlaybook,
} from "@repo/app-types/entities/playbook";
import { buildTeammateDisplayNames } from "@repo/app-types/entities/teammate";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { getIsTrainingProject } from "@repo/app-types/helpers/utilities";
import type { ChatSuggestion } from "@repo/ui/chat/chatEmptyState";
import { usePlaybookByIdQuery } from "../../api/entities/playbook.js";
import { useProjectByIdQuery } from "../../api/entities/project.js";
import { useBasicTeammateQuery } from "../../api/entities/teammate.js";
import { useGetTeammateAgentQuery } from "../../api/entities/teammateAgent.js";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import {
  resolveCopilotSurface,
  selectCopilotSuggestionIds,
  type CopilotSuggestionData,
} from "./copilotRouteRegistry.js";
import { useCopilotPageContext } from "./researchAgentPageContext.js";

/**
 * Resolves the names and flags the starters read from the ids the page's
 * entry selects. Every query is called on every page and skipped when its id
 * is absent, so hook order never depends on which page is open.
 */
function useCopilotSuggestionData(
  descriptor: PageContextDescriptor | undefined,
): CopilotSuggestionData {
  const {
    playbookId,
    teammateId,
    teammateAgentId,
    projectId,
    criterionId,
    signalId,
  } = selectCopilotSuggestionIds(descriptor);

  const { data: playbook } = usePlaybookByIdQuery(playbookId ?? skipToken);
  const { data: teammate } = useBasicTeammateQuery(teammateId ?? skipToken);
  const { data: teammateAgent } = useGetTeammateAgentQuery(
    teammateAgentId ?? skipToken,
  );
  const { data: project } = useProjectByIdQuery(projectId ?? skipToken);
  const isKnowledgeBaseEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.KnowledgeBase,
  );

  const outcomeDefinition = playbook
    ? getOutcomeDefinitionFromPlaybook(playbook)
    : undefined;

  return {
    outcomeDefinition,
    outcomeName: outcomeDefinition?.name.toLowerCase(),
    playbookTree: playbook?.assessment_tree,
    isKnowledgeBaseEnabled: isKnowledgeBaseEnabled ?? false,
    teammateId,
    teammateName: teammate ? formatTeammateName(teammate) : undefined,
    criterionName:
      playbook && criterionId
        ? collectPlaybookCriteria(playbook.assessment_tree).find(
            ({ id }) => id === criterionId,
          )?.name
        : undefined,
    agentName: teammateAgent?.name ?? "this agent",
    signalId,
    isTrainingProject: project ? getIsTrainingProject(project) : false,
  };
}

/**
 * The secondary name is usually the email, which tells apart teammates who
 * share a display name.
 */
function formatTeammateName(
  teammate: Parameters<typeof buildTeammateDisplayNames>[0],
): string {
  const { primaryDisplayName, secondaryDisplayName } =
    buildTeammateDisplayNames(teammate);
  return secondaryDisplayName
    ? `${primaryDisplayName} (${secondaryDisplayName})`
    : primaryDisplayName;
}

/**
 * Starters for the copilot's empty state, chosen by the page the user is on.
 * The page's registry entry decides which set; this only resolves the names
 * those sets read.
 */
export function useCopilotSuggestions(): ChatSuggestion[] {
  const descriptor = useCopilotPageContext();
  const data = useCopilotSuggestionData(descriptor);
  return resolveCopilotSurface(descriptor).buildSuggestions(data);
}
